import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  Signal,
  viewChild,
  WritableSignal,
} from '@angular/core';
import { Location } from '@angular/common';
import { UpperFirstLetterPipe } from '../../pipes/upper-first-letter-pipe';
import { TranslateModule } from '@ngx-translate/core';
import { IconComponent } from '@kinomap-dev/ng-ui-lib/basics';
import { ContentPreview } from '../../components/content-creation-preview/content-preview';
import { ContentTypeBlock } from '../../components/content-type-block/content-type-block';
import { CreationScenicForm } from '../../components/creation-scenic-form/creation-scenic-form';
import { CreationCoachingForm } from '../../components/creation-coaching-form/creation-coaching-form';
import { CreationWorkoutForm } from '../../components/creation-workout-form/creation-workout-form';
import { CreationGpsOnlyForm } from '../../components/creation-gps-track-form/creation-gps-only-form';
import { CanComponentDeactivate } from '../../guards/can-component-deactivate.interface';
import { filter, Observable, switchMap, tap } from 'rxjs';
import { CreateContentService } from '../../services/create-content.service';
import { FormGroup } from '@angular/forms';
import { AnalyticsService } from '../../services/analytics.service';
import { collectFormErrors, FormFieldError } from '../../utils/form-errors';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { HttpResourceRef } from '@angular/common/http';
import { IconType, Information } from '@kinomap-dev/ng-ui-lib/core';
import { TranslateService } from '@ngx-translate/core';
import { ApiService } from '../../services/api.service';
import { UploadService } from '../../services/upload-service';
import { Toaster } from '../../services/toaster';
import { ModalService } from '../../services/modal-service';
import { ColorService } from '../../services/color.service';
import { DialogRef } from '@angular/cdk/dialog';
import { WorkoutDetails } from '../../types/workout-details.type';
import {
  ContentAvailabilityBlock,
  Disponibility,
} from '../../components/content-availability-block/content-availability-block';

export type ContentType = 'scenic' | 'coaching' | 'workout' | 'gps-only' | 'playlist';
export type CreationContentFormType =
  CreationScenicForm | CreationCoachingForm | CreationWorkoutForm | CreationGpsOnlyForm | undefined;
@Component({
  selector: 'app-content-creation',
  imports: [
    UpperFirstLetterPipe,
    TranslateModule,
    IconComponent,
    ContentPreview,
    ContentTypeBlock,
    ContentAvailabilityBlock,
    CreationScenicForm,
    CreationCoachingForm,
    CreationWorkoutForm,
    CreationGpsOnlyForm,
    UpperFirstLetterPipe,
  ],
  templateUrl: './content-creation.html',
  styleUrl: './content-creation.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ContentCreation implements CanComponentDeactivate {
  protected location: Location = inject(Location);
  private router: Router = inject(Router);
  private createContentService: CreateContentService = inject(CreateContentService);
  private analyticsService: AnalyticsService = inject(AnalyticsService);
  private apiService: ApiService = inject(ApiService);
  private uploadService: UploadService = inject(UploadService);
  private toaster: Toaster = inject(Toaster);
  private translateService: TranslateService = inject(TranslateService);
  private modalService: ModalService = inject(ModalService);
  protected colorService: ColorService = inject(ColorService);

  private readonly routeParams: ParamMap = inject(ActivatedRoute).snapshot.paramMap;
  private readonly editedType: ContentType | undefined =
    (this.routeParams.get('type') as ContentType | null) ?? undefined;
  protected readonly editedId: string | null = this.routeParams.get('id');
  protected readonly editedContentResource: HttpResourceRef<Information> =
    this.apiService.getContentDetailsResource(
      this.editedType === 'workout' ? 'workout' : undefined,
      this.editedId,
    );

  protected readonly disponibilities: Signal<Disponibility[]> = computed(() => {
    const content: Information | undefined = this.editedContentResource.hasValue()
      ? this.editedContentResource.value()
      : undefined;
    return [
      { icon: 'language' as IconType, label: 'content.availability.website' },
      {
        icon: 'computer' as IconType,
        label: 'content.availability.app',
        disabled: content?.status === 'deleted',
      },
    ];
  });

  readonly contentType: WritableSignal<ContentType | undefined> =
    this.createContentService.contentType;
  protected readonly isDeleted: WritableSignal<boolean> = this.createContentService.isDeleted;

  private readonly scenicForm: Signal<CreationScenicForm | undefined> = viewChild(
    CreationScenicForm,
    {
      read: CreationScenicForm,
    },
  );
  private readonly coachingForm: Signal<CreationCoachingForm | undefined> = viewChild(
    CreationCoachingForm,
    {
      read: CreationCoachingForm,
    },
  );
  private readonly workoutForm: Signal<CreationWorkoutForm | undefined> = viewChild(
    CreationWorkoutForm,
    {
      read: CreationWorkoutForm,
    },
  );
  private readonly gpsOnlyForm: Signal<CreationGpsOnlyForm | undefined> = viewChild(
    CreationGpsOnlyForm,
    {
      read: CreationGpsOnlyForm,
    },
  );

  constructor() {
    if (this.editedId) this.createContentService.reset();

    effect(() => {
      if (!this.editedContentResource.hasValue()) return;
      this.createContentService.loadWorkout(this.editedContentResource.value() as WorkoutDetails);
    });

    effect(() => {
      const error: unknown = this.editedContentResource.error();
      if (!error) return;
      console.error('content_details.errors.content', error);
      this.toaster.error(
        this.translateService.instant('common.error'),
        this.translateService.instant('content_details.errors.content'),
        'warning',
      );
    });
  }

  private trackFormErrors(): void {
    const form: FormGroup | undefined =
      this.scenicForm()?.scenicForm ??
      this.coachingForm()?.coachingForm ??
      this.workoutForm()?.workoutForm ??
      this.gpsOnlyForm()?.gpsTrackForm;
    if (!form?.invalid) return;

    collectFormErrors(form).forEach(({ field, error }: FormFieldError) =>
      this.analyticsService.track('form_error', {
        content_type: this.contentType(),
        field,
        error,
      }),
    );
  }

  save(): void {
    this.saveEditedContent().subscribe((saved: boolean) => {
      if (saved) this.router.navigate(['contents', this.editedType, this.editedId]);
    });
  }

  saveEditedContent(): Observable<boolean> {
    return this.uploadService.updateWorkout().pipe(
      tap((saved: boolean) => {
        if (saved) {
          this.workoutForm()?.workoutForm.markAsPristine();
          this.toaster.success(
            undefined,
            this.translateService.instant('content_creation_preview.save_success'),
            'g_validate',
          );
        } else {
          this.toaster.error(
            this.translateService.instant('content_creation_preview.errors.title'),
            this.translateService.instant('content_creation_preview.errors.save_failed'),
            'warning',
          );
        }
      }),
    );
  }

  deleteContent(): void {
    const dialogRef: DialogRef<'delete' | 'cancel', unknown> = this.modalService.openConfirmation<
      'delete' | 'cancel'
    >({
      title: 'confirm_delete.title',
      message: 'confirm_delete.message',
      buttons: [
        { action: 'cancel', label: 'common.cancel', style: 'secondary' },
        { action: 'delete', label: 'common.delete', style: 'danger' },
      ],
    });

    dialogRef.closed
      .pipe(
        filter((action: 'delete' | 'cancel' | undefined) => action === 'delete'),
        switchMap(() => this.uploadService.deleteWorkout()),
      )
      .subscribe((deleted: boolean) => {
        if (!deleted) {
          this.toaster.error(
            this.translateService.instant('common.error'),
            this.translateService.instant('confirm_delete.error'),
            'warning',
          );
          return;
        }

        this.toaster.success(
          undefined,
          this.translateService.instant('confirm_delete.success'),
          'g_validate',
        );
        this.router.navigate(['contents', this.editedType]);
      });
  }

  onSubmitAttempted(): void {
    const activeForm: CreationContentFormType =
      this.scenicForm() || this.coachingForm() || this.workoutForm() || this.gpsOnlyForm();

    if (activeForm && 'markAllErrors' in activeForm) {
      (activeForm as { markAllErrors(): void }).markAllErrors();
    }

    this.trackFormErrors();
  }

  contentTypeChange(newContentType: ContentType): void {
    this.contentType.set(newContentType);
  }

  canDeactivate(): Observable<boolean> | boolean {
    const activeForm: CreationContentFormType =
      this.scenicForm() || this.coachingForm() || this.workoutForm() || this.gpsOnlyForm();

    if (activeForm instanceof CreationWorkoutForm && this.editedId) {
      return activeForm.canDeactivate();
    }
    if (activeForm && 'canDeactivate' in activeForm) {
      return activeForm.canDeactivate();
    }
    return true;
  }

  ngOnDestroy(): void {
    if (this.editedId) this.createContentService.reset();
  }
}
